import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar, QrCode, Volume2, Camera, Video, Monitor, Headphones, Globe, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Calendar, QrCode, Volume2, Camera, Video, Monitor, Headphones, Globe,
  // Aliases for legacy
  Ticket: Calendar,
};

const FALLBACK_CARDS = [
  { id: "plateforme", title: "Plateforme evenementielle", badge: "Inscriptions & QR", description: "Inscriptions en ligne, QR Codes individuels, controle des entrees et statistiques en temps reel.", icon: "Calendar", cta_url: "/evenements", cta_text: "Demander un acces" },
  { id: "sono", title: "Sonorisation professionnelle", badge: "Audiovisuel", description: "Enceintes, microphones, mixage et installation sur mesure pour vos evenements.", icon: "Volume2", cta_url: "/devis", cta_text: "Demander un devis" },
  { id: "photo", title: "Reportage photo & video", badge: "Couverture media", description: "Photographes et cameramans 4K pour immortaliser chaque moment de votre evenement.", icon: "Camera", cta_url: "/devis", cta_text: "Demander un devis" },
  { id: "regie", title: "Regie audiovisuelle", badge: "Production live", description: "Streaming HD, ecrans LED, regie multicamera et traduction simultanee.", icon: "Video", cta_url: "/devis", cta_text: "Demander un devis" },
  { id: "captation", title: "Captation audio", badge: "Enregistrement", description: "Captation et enregistrement audio professionnel de conferences et discours.", icon: "Headphones", cta_url: "/devis", cta_text: "Demander un devis" },
  { id: "digital", title: "Services digitaux", badge: "GLOTECH Digital", description: "Sites web, applications, SEO et solutions digitales pour votre organisation.", icon: "Globe", cta_url: "/devis", cta_text: "En savoir plus" },
];

// Individual content for each service type
const SERVICE_CONTENT = {
  "Plateforme evenementielle": {
    badge: "Event Center",
    description: "Inscriptions en ligne, QR Codes individuels, controle des entrees et statistiques en temps reel.",
    icon: "Calendar",
    cta_url: "/evenements",
    cta_text: "Demander mon acces gratuit",
  },
  "Sonorisation professionnelle": {
    badge: "Audiovisuel",
    description: "Enceintes, microphones, console de mixage, installation et assistance technique pour vos evenements.",
    icon: "Volume2",
    cta_url: "/devis",
    cta_text: "Demander un devis",
  },
  "Reportage photo & video": {
    badge: "Couverture media",
    description: "Couverture professionnelle de vos evenements, photographie et captation video 4K.",
    icon: "Camera",
    cta_url: "/devis",
    cta_text: "Demander un devis",
  },
  "Regie audiovisuelle": {
    badge: "Production live",
    description: "Gestion et captation professionnelle des flux audio et video, streaming HD, ecrans LED.",
    icon: "Video",
    cta_url: "/devis",
    cta_text: "Demander un devis",
  },
  "Captation audio": {
    badge: "Enregistrement",
    description: "Captation et enregistrement audio professionnel de conferences et discours.",
    icon: "Headphones",
    cta_url: "/devis",
    cta_text: "Demander un devis",
  },
  "Services digitaux": {
    badge: "GLOTECH Digital",
    description: "Sites web, applications, SEO et solutions digitales pour votre organisation.",
    icon: "Globe",
    cta_url: "/devis",
    cta_text: "En savoir plus",
  },
};

export default function DynamicAdSidebar() {
  const [cards, setCards] = useState(FALLBACK_CARDS);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await fetch(`${API}/api/ad-campaigns/public?placement=sidebar_right`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.length === 0) return;

        const allCards = [];

        for (const c of data) {
          if (c.services && c.services.length > 1) {
            // Expand campaign services into individual cards with unique content
            c.services.forEach((svc, i) => {
              const svcContent = SERVICE_CONTENT[svc] || {};
              allCards.push({
                id: `${c.id}-${i}`,
                campaign_id: c.id,
                title: svc,
                badge: svcContent.badge || c.advertiser,
                description: svcContent.description || svc,
                icon: svcContent.icon || "Calendar",
                cta_url: svcContent.cta_url || c.cta_url || "/devis",
                cta_text: svcContent.cta_text || "En savoir plus",
                image_url: c.image_url,
              });
            });
          } else {
            allCards.push({
              id: c.id,
              campaign_id: c.id,
              title: c.title,
              badge: c.services?.[0] || c.advertiser,
              description: c.description?.split("\n")[0] || "",
              icon: "Calendar",
              cta_url: c.cta_url || "/devis",
              cta_text: c.cta_text || "En savoir plus",
              image_url: c.image_url,
            });
          }

          // Track impression
          fetch(`${API}/api/ad-campaigns/public/${c.id}/impression`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ placement: "sidebar_right" }),
          }).catch(() => {});
        }

        if (allCards.length > 0) setCards(allCards);
      } catch {
        // Keep fallback cards
      }
    };
    fetchCampaigns();
  }, []);

  const next = useCallback(() => setActiveIndex(i => (i + 1) % cards.length), [cards.length]);
  const prev = useCallback(() => setActiveIndex(i => (i - 1 + cards.length) % cards.length), [cards.length]);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(next, 5000);
    return () => clearInterval(intervalRef.current);
  }, [paused, next]);

  const handleClick = (card) => {
    if (card.campaign_id) {
      fetch(`${API}/api/ad-campaigns/public/${card.campaign_id}/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placement: "sidebar_right" }),
      }).catch(() => {});
    }
    if (card.cta_url?.startsWith("/")) navigate(card.cta_url);
    else if (card.cta_url) window.open(card.cta_url, "_blank");
  };

  return (
    <div
      data-testid="ad-sidebar-container"
      className="hidden xl:block w-80 shrink-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="sticky top-24 space-y-4 pr-6 pt-8">
        {/* Title */}
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-mono uppercase tracking-widest text-[#64748b]">Nos prestations</span>
          <div className="flex gap-1">
            <button data-testid="ad-sidebar-prev-btn" onClick={prev} className="p-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#0099CC]/50 transition-all">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button data-testid="ad-sidebar-next-btn" onClick={next} className="p-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#0099CC]/50 transition-all">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Card Carousel */}
        <div className="relative overflow-hidden rounded-2xl border border-[#2A3441] bg-[#0F1420]/90 backdrop-blur-md shadow-xl">
          {cards.map((card, idx) => {
            const Icon = ICON_MAP[card.icon] || Calendar;
            return (
              <div
                key={card.id}
                data-testid="ad-sidebar-carousel-card"
                className={`transition-all duration-500 ${idx === activeIndex ? "opacity-100 relative" : "opacity-0 absolute inset-0 pointer-events-none"}`}
              >
                {card.image_url && (
                  <div className="h-40 overflow-hidden">
                    <img src={card.image_url} alt={card.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}

                <div className="p-5">
                  {/* Badge */}
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#0099CC] bg-[#0099CC]/10 border border-[#0099CC]/20 rounded-full px-2.5 py-1 mb-3">
                    <Icon className="h-3 w-3" />
                    {card.badge}
                  </span>

                  <h3 className="text-lg font-bold text-white tracking-tight mb-2">{card.title}</h3>
                  <p className="text-[#94A3B8] text-sm leading-relaxed mb-4">{card.description}</p>

                  <button
                    onClick={() => handleClick(card)}
                    className="w-full flex items-center justify-center gap-2 bg-[#0099CC]/10 hover:bg-[#0099CC]/20 border border-[#0099CC]/30 text-[#0099CC] hover:text-white py-2.5 rounded-xl text-sm font-semibold transition-all"
                  >
                    {card.cta_text || "En savoir plus"}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Indicators */}
        <div className="flex justify-center gap-1.5">
          {cards.map((_, idx) => (
            <button
              key={idx}
              data-testid="ad-sidebar-indicator"
              onClick={() => setActiveIndex(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${idx === activeIndex ? "w-6 bg-[#0099CC]" : "w-1.5 bg-[#2A3441] hover:bg-[#4A5568]"}`}
            />
          ))}
        </div>

        {/* Counter */}
        <p className="text-center text-[10px] text-[#4A5568] font-mono">{activeIndex + 1} / {cards.length}</p>
      </div>
    </div>
  );
}
